import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';

type ConfigFormData = {
    pageTitleDe: string;
    pageTitleEn: string;
    progressDisplayDe: string;
    progressDisplayEn: string;
    appointmentDetailsTitleDe: string;
    appointmentDetailsTitleEn: string;
    contactInfoTitleDe: string;
    contactInfoTitleEn: string;
    treatmentsTitleDe: string;
    treatmentsTitleEn: string;
    discountCodePlaceholderDe: string;
    discountCodePlaceholderEn: string;
    discountCodeButtonTextDe: string;
    discountCodeButtonTextEn: string;
    earlierTimeslotTextDe: string;
    earlierTimeslotTextEn: string;
    bookNowButtonTextDe: string;
    bookNowButtonTextEn: string;
    // New fields
    totalLabelDe: string;
    totalLabelEn: string;
    messageLabelDe: string;
    messageLabelEn: string;
    messagePlaceholderDe: string;
    messagePlaceholderEn: string;
    discountCodeTitleDe: string;
    discountCodeTitleEn: string;
};

const BookingReviewCMS = () => {
    const [formData, setFormData] = useState<ConfigFormData>({
        pageTitleDe: '',
        pageTitleEn: '',
        progressDisplayDe: '',
        progressDisplayEn: '',
        appointmentDetailsTitleDe: '',
        appointmentDetailsTitleEn: '',
        contactInfoTitleDe: '',
        contactInfoTitleEn: '',
        treatmentsTitleDe: '',
        treatmentsTitleEn: '',
        discountCodePlaceholderDe: '',
        discountCodePlaceholderEn: '',
        discountCodeButtonTextDe: '',
        discountCodeButtonTextEn: '',
        earlierTimeslotTextDe: '',
        earlierTimeslotTextEn: '',
        bookNowButtonTextDe: '',
        bookNowButtonTextEn: '',
        // New fields
        totalLabelDe: '',
        totalLabelEn: '',
        messageLabelDe: '',
        messageLabelEn: '',
        messagePlaceholderDe: '',
        messagePlaceholderEn: '',
        discountCodeTitleDe: '',
        discountCodeTitleEn: '',
    });
    const [isDirty, setIsDirty] = useState(false);

    const utils = trpc.useUtils();
    const { data: configData, isLoading } = trpc.bookingFunnelCMS.getBookingReviewConfig.useQuery();

    const updateMutation = trpc.bookingFunnelCMS.updateBookingReviewConfig.useMutation({
        onSuccess: () => {
            toast.success('Konfiguration gespeichert');
            setIsDirty(false);
            utils.bookingFunnelCMS.getBookingReviewConfig.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    useEffect(() => {
        if (configData?.data) {
            const config = configData.data;
            setFormData({
                pageTitleDe: config.pageTitleDe,
                pageTitleEn: config.pageTitleEn,
                progressDisplayDe: config.progressDisplayDe,
                progressDisplayEn: config.progressDisplayEn,
                appointmentDetailsTitleDe: config.appointmentDetailsTitleDe,
                appointmentDetailsTitleEn: config.appointmentDetailsTitleEn,
                contactInfoTitleDe: config.contactInfoTitleDe,
                contactInfoTitleEn: config.contactInfoTitleEn,
                treatmentsTitleDe: config.treatmentsTitleDe,
                treatmentsTitleEn: config.treatmentsTitleEn,
                discountCodePlaceholderDe: config.discountCodePlaceholderDe,
                discountCodePlaceholderEn: config.discountCodePlaceholderEn,
                discountCodeButtonTextDe: config.discountCodeButtonTextDe,
                discountCodeButtonTextEn: config.discountCodeButtonTextEn,
                earlierTimeslotTextDe: config.earlierTimeslotTextDe,
                earlierTimeslotTextEn: config.earlierTimeslotTextEn,
                bookNowButtonTextDe: config.bookNowButtonTextDe,
                bookNowButtonTextEn: config.bookNowButtonTextEn,
                // New fields
                totalLabelDe: config.totalLabelDe ?? '',
                totalLabelEn: config.totalLabelEn ?? '',
                messageLabelDe: config.messageLabelDe ?? '',
                messageLabelEn: config.messageLabelEn ?? '',
                messagePlaceholderDe: config.messagePlaceholderDe ?? '',
                messagePlaceholderEn: config.messagePlaceholderEn ?? '',
                discountCodeTitleDe: config.discountCodeTitleDe ?? '',
                discountCodeTitleEn: config.discountCodeTitleEn ?? '',
            });
        }
    }, [configData]);

    const handleChange = (field: keyof ConfigFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setIsDirty(true);
    };

    const handleSave = () => {
        updateMutation.mutate(formData);
    };

    if (isLoading) {
        return <Skeleton className='h-96 w-full' />;
    }

    const renderFieldPair = (labelDe: string, labelEn: string, fieldDe: keyof ConfigFormData, fieldEn: keyof ConfigFormData, multiline = false) => (
        <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
                <Label>{labelDe}</Label>
                {multiline ? (
                    <Textarea
                        value={formData[fieldDe]}
                        onChange={(e) => handleChange(fieldDe, e.target.value)}
                        rows={3}
                    />
                ) : (
                    <Input
                        value={formData[fieldDe]}
                        onChange={(e) => handleChange(fieldDe, e.target.value)}
                    />
                )}
            </div>
            <div className='space-y-2'>
                <Label>{labelEn}</Label>
                {multiline ? (
                    <Textarea
                        value={formData[fieldEn]}
                        onChange={(e) => handleChange(fieldEn, e.target.value)}
                        rows={3}
                    />
                ) : (
                    <Input
                        value={formData[fieldEn]}
                        onChange={(e) => handleChange(fieldEn, e.target.value)}
                    />
                )}
            </div>
        </div>
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle>Buchungsübersicht CMS</CardTitle>
                <CardDescription>
                    Verwalten Sie die Texte und Beschriftungen für die Buchungsübersichts-Seite.
                </CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
                <div className='flex justify-end'>
                    <Button onClick={handleSave} disabled={!isDirty || updateMutation.isPending}>
                        {updateMutation.isPending ? (
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        ) : (
                            <Save className='mr-2 h-4 w-4' />
                        )}
                        Speichern
                    </Button>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Seiten-Header</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Seitentitel (DE)', 'Page Title (EN)', 'pageTitleDe', 'pageTitleEn')}
                        {renderFieldPair('Fortschrittsanzeige (DE)', 'Progress Display (EN)', 'progressDisplayDe', 'progressDisplayEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Abschnitts-Titel</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Termindetails Titel (DE)', 'Appointment Details Title (EN)', 'appointmentDetailsTitleDe', 'appointmentDetailsTitleEn')}
                        {renderFieldPair('Kontaktinfo Titel (DE)', 'Contact Info Title (EN)', 'contactInfoTitleDe', 'contactInfoTitleEn')}
                        {renderFieldPair('Behandlungen Titel (DE)', 'Treatments Title (EN)', 'treatmentsTitleDe', 'treatmentsTitleEn')}
                        {renderFieldPair('Gesamt Label (DE)', 'Total Label (EN)', 'totalLabelDe', 'totalLabelEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Nachricht</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Nachricht Label (DE)', 'Message Label (EN)', 'messageLabelDe', 'messageLabelEn')}
                        {renderFieldPair('Nachricht Platzhalter (DE)', 'Message Placeholder (EN)', 'messagePlaceholderDe', 'messagePlaceholderEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Rabattcode</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Rabattcode Titel (DE)', 'Discount Code Title (EN)', 'discountCodeTitleDe', 'discountCodeTitleEn')}
                        {renderFieldPair('Rabattcode Platzhalter (DE)', 'Discount Code Placeholder (EN)', 'discountCodePlaceholderDe', 'discountCodePlaceholderEn')}
                        {renderFieldPair('Rabattcode Button (DE)', 'Discount Code Button (EN)', 'discountCodeButtonTextDe', 'discountCodeButtonTextEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Früherer Termin</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Früherer Termin Text (DE)', 'Earlier Timeslot Text (EN)', 'earlierTimeslotTextDe', 'earlierTimeslotTextEn', true)}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Jetzt Buchen Button</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Jetzt Buchen (DE)', 'Book Now (EN)', 'bookNowButtonTextDe', 'bookNowButtonTextEn')}
                    </CardContent>
                </Card>
            </CardContent>
        </Card>
    );
};

export default BookingReviewCMS;
